<script setup lang="ts">
import AppFlicker from '../appLoaders/appFlicker/AppFlicker.vue';

interface AppPlaceholderLayoutProps {
  title: string;
  description?: string;
  isSmallDevice: boolean;
  isLoading?: boolean;
  isPrintMode?: boolean;
}

const props = defineProps<AppPlaceholderLayoutProps>();
</script>

<template>
  <div class="app-placeholder-layout" :class="{ mobile: props.isSmallDevice }">
    <div class="left-content">
      <slot name="left-content" />
    </div>
    <div class="main-content">
      <div class="text-wrapper">
        <div class="title">{{ props.title }}</div>
        <AppFlicker
          v-if="!props.description"
          width="spacing-96"
          :height="props.isSmallDevice ? 'font-size-m' : 'font-size-l'"
        />
        <div v-else class="description">{{ props.description }}</div>
      </div>
      <template v-if="!props.isPrintMode">
        <div v-if="props.isSmallDevice" class="actions-wrapper">
          <slot name="actions" />
        </div>
        <div v-else class="actions-wrapper" @click.stop>
          <slot name="actions" />
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.app-placeholder-layout {
  display: flex;
  flex-direction: row;
  padding: var(--spacing-16) var(--spacing-16) var(--spacing-16)
    var(--spacing-4);
  gap: var(--spacing-4);
  border-radius: var(--border-radius-8);
  border: 1px dashed var(--color-neutral-30);
  background-color: var(--color-white);
  width: auto;
  align-items: center;

  .left-content {
    min-width: 60px;
    display: flex;
    justify-content: center;
  }

  .main-content {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: var(--spacing-12);
    color: var(--color-neutral-70);
    width: 100%;

    .text-wrapper {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-8);

      .title {
        @include font-label-l-bold;
      }

      .description {
        @include font-body-m-light;
      }
    }

    .actions-wrapper {
      @include font-body-m-bold;
      text-decoration: underline;
      display: flex;
      flex-wrap: nowrap;
      flex-direction: row;
      column-gap: var(--spacing-24);
      row-gap: var(--spacing-4);
      align-items: center;
      & > * {
        white-space: nowrap;
      }
    }
  }
  &.mobile {
    align-items: flex-start;

    .left-content {
      min-width: 40px;
    }

    .main-content {
      flex-direction: column;

      .text-wrapper {
        gap: var(--spacing-4);

        .title {
          @include font-label-m-bold;
        }

        .description {
          @include font-body-s-light;
        }
      }

      .actions-wrapper {
        @include font-body-s-bold;
        flex-wrap: wrap;
      }
    }
  }
}
</style>
